import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  FileDuplicateWarning,
  IngestedFile,
  SkippedUpload,
  SyntiroHoneyFileStatus
} from '../../../types/syntiro_honey';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import { formatByteSize } from './upload_validation';

type ChipColor = 'default' | 'info' | 'success' | 'error';

const FILE_STATUS_CHIPS: Record<SyntiroHoneyFileStatus, { label: string; color: ChipColor }> = {
  UPLOADED: { label: 'Uploaded', color: 'default' },
  QUEUED: { label: 'Queued', color: 'info' },
  PROCESSING: { label: 'Processing', color: 'info' },
  PROCESSED: { label: 'Processed', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' }
};

/**
 * The upload timestamp of the matched file for display, or null when it cannot
 * be shown honestly (a within-batch match has none, and an unparseable one must
 * never reach the reviewer as "Invalid Date").
 * @param {string | null} matchedAt ISO 8601 upload time of the matched file, or null.
 * @returns {string | null} The localised timestamp, or null when unavailable.
 */
function formatMatchedAt(matchedAt: string | null): string | null {
  if (matchedAt === null) {
    return null;
  }
  const matched_ms = Date.parse(matchedAt);
  return Number.isNaN(matched_ms) ? null : new Date(matched_ms).toLocaleString();
}

/** The matched file described with its upload time when known. */
function describeMatch(duplicate: FileDuplicateWarning): string {
  const uploaded_at = formatMatchedAt(duplicate.matchedAt);
  if (uploaded_at !== null) {
    return `"${duplicate.matchedFilename}", uploaded ${uploaded_at}`;
  }
  // No timestamp: a match with no prior file id is another file of THIS batch.
  return duplicate.matchedFileId === null
    ? `another file in this batch ("${duplicate.matchedFilename}")`
    : `"${duplicate.matchedFilename}", an earlier upload`;
}

/**
 * Explain a duplicate match before the reviewer confirms the batch. Duplicates are
 * an explicit CHOICE, never a silent decision (Maxine 2026-08-19): an exact copy
 * must be removed or acknowledged; a same-name file processes normally and any
 * record overlaps are resolved in review afterwards.
 * @param {FileDuplicateWarning} duplicate The match reported by `POST /batches`.
 * @returns {string} The warning sentence for the file's row.
 */
export function describeDuplicate(duplicate: FileDuplicateWarning): string {
  const matched = describeMatch(duplicate);
  if (duplicate.kind === 'EXACT_CONTENT') {
    return (
      `This file is an exact copy of ${matched}. ` +
      'Remove it, or confirm below to process it anyway — every row will be added as a new record.'
    );
  }
  return (
    `This file has the same name as ${matched}, but different content. ` +
    'It will process normally; if some of its values already exist on Syntiro, ' +
    'you will choose how to handle them on the review screen. For a corrected ' +
    're-upload of a file you uploaded yourself, remove it and upload it again ' +
    'with "Replaces an earlier file" set in its file context — that list only ' +
    'offers your own earlier uploads.'
  );
}

interface BatchFileListProps {
  /** Persisted file rows of the created batch. */
  files: Array<IngestedFile>;
  /** Files the backend refused during upload, with reasons. */
  skipped: Array<SkippedUpload>;
  /** Soft-delete a file from the batch before it is confirmed. */
  onRemoveFile: (id: string) => void;
  /** Id of the file currently being removed (shows a spinner), or null. */
  removingFileId: string | null;
  /** Disable removal (e.g. once processing has started). */
  disabled?: boolean;
  /** Exact-duplicate files the uploader confirmed to process as new records. */
  acknowledgedFileIds?: ReadonlySet<string>;
  /** Toggle a file's "process as new records" acknowledgement. */
  onAcknowledgeAsNew?: (id: string, acknowledged: boolean) => void;
}

/**
 * The persisted batch file rows returned by `POST /batches`: filename, size
 * and pipeline status per file, with a remove (soft-delete) button while the
 * batch is still unconfirmed. An exact-content duplicate additionally requires a
 * choice — remove, or acknowledge "process as new records" — before the batch can
 * be confirmed (Maxine 2026-08-19 scenario 1). Also surfaces server-side skipped
 * uploads.
 * @param {Array<IngestedFile>} files Persisted batch file rows.
 * @param {Array<SkippedUpload>} skipped Server-rejected uploads with reasons.
 * @param {(id: string) => void} onRemoveFile Soft-delete handler.
 * @param {string | null} removingFileId File id with a delete in flight, or null.
 * @param {boolean | undefined} disabled Disable removal.
 * @param {ReadonlySet<string> | undefined} acknowledgedFileIds Confirmed process-as-new file ids.
 * @param {((id: string, acknowledged: boolean) => void) | undefined} onAcknowledgeAsNew Acknowledgement toggle.
 * @returns {JSX.Element}
 */
export default function BatchFileList({
  files,
  skipped,
  onRemoveFile,
  removingFileId,
  disabled,
  acknowledgedFileIds,
  onAcknowledgeAsNew
}: BatchFileListProps) {
  return (
    <Box>
      {skipped.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{`${skipped.length} file${skipped.length === 1 ? '' : 's'} skipped by the server`}</AlertTitle>
          {skipped.map((skip: SkippedUpload) => (
            <BodyText key={skip.filename}>{`${skip.filename}: ${skip.reason}`}</BodyText>
          ))}
        </Alert>
      )}
      <List disablePadding>
        {files.map((file: IngestedFile) => {
          const status_chip = FILE_STATUS_CHIPS[file.status];
          const removing = removingFileId === file.id;
          const duplicate = file.duplicate ?? null;
          const needs_choice =
            duplicate?.kind === 'EXACT_CONTENT' && onAcknowledgeAsNew !== undefined;
          const acknowledged = acknowledgedFileIds?.has(file.id) ?? false;
          return (
            <ListItem
              key={file.id}
              disableGutters
              sx={{ borderBottom: 1, borderColor: 'divider', display: 'block' }}
              secondaryAction={
                removing ? (
                  <CircularProgress size={20} />
                ) : (
                  <Tooltip title="Remove file from batch">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveFile(file.id)}
                        disabled={disabled || removingFileId !== null}
                        aria-label={`Remove ${file.filename}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <BodyText weight="Medium">{file.filename}</BodyText>
                  <CaptionText>{formatByteSize(file.byteSize)}</CaptionText>
                </Box>
                <Chip size="small" label={status_chip.label} color={status_chip.color} />
              </Box>
              {duplicate !== null && (
                // Raised here rather than after processing: this is the last point
                // at which the reviewer can drop the file. `mr` keeps clear of the
                // remove button, which MUI anchors vertically centred over the row.
                <Alert
                  severity={duplicate.kind === 'EXACT_CONTENT' ? 'error' : 'warning'}
                  sx={{ mt: 0.5, mb: 1, mr: 5, py: 0 }}
                >
                  {describeDuplicate(duplicate)}
                  {needs_choice && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={acknowledged}
                            onChange={(event) => onAcknowledgeAsNew(file.id, event.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label="Process as new records"
                      />
                    </Box>
                  )}
                </Alert>
              )}
            </ListItem>
          );
        })}
      </List>
      {files.length === 0 && (
        <BodyText sx={{ py: 2 }}>
          No files left in this batch — every file was removed or skipped. Start over to upload
          again.
        </BodyText>
      )}
    </Box>
  );
}
